import moment from 'moment';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import * as toast from 'utils/toast';
import {
  useFetchTeamManagerHistoryQuery,
  useFetchUserByIdQuery,
  useUpdateTeamManagerMutation,
} from 'hooks/query/useUserQueries';
import { extractFullName } from 'utils/string';

import useTitle from 'hooks/useTitle';
import Loading from 'components/common/loading/Loading';
import TeamManagersHistory from './TeamManagersHistory';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';
import ProfileHeader from '../components/profileHeader/ProfileHeader';
import AvatarComponent from 'components/common/avatar/AvatarComponent';
import useEmployeeDebounceOptions from 'hooks/useEmployeeDebounceOptions';
import DebounceFormDropdown from 'components/common/debounceFormDropdown/DebounceFormDropdown';
import { LIST_TYPE, REACT_DATEPICKER_DATE_FORMAT, ROLES_IDS, USER_FETCH_FIELDS } from 'constants/appConstants';
import type { EmployeeLike } from 'interface/userUtils';
type ProfileHeaderEmployee = React.ComponentProps<typeof ProfileHeader>['employee'];
type ProfileHeaderUser = React.ComponentProps<typeof ProfileHeader>['user'];

interface TeamManagerOption {
  label: string;
  value: number | string;
}

interface TeamManagerHistoryItem {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  transitionDate: string;
  createdAt: string;
}
interface NameFields {
  firstName: string;
  middleName?: string | null;
  lastName: string;
}

interface ChangeTeamManagerProps extends RouteComponentProps<{ id?: string }> {
  user: ProfileHeaderUser;
  match: { params: { id?: string } };
}

const ChangeTeamManager: React.FC<ChangeTeamManagerProps> = (props) => {
  const id = props.match.params.id;
  const [selectedTeamManager, setSelectedTeamManager] = useState<number | string | null>(null);
  const [transitionDate, setTransitionDate] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const setTitle = useTitle();

  const { data: employeeData, isLoading: loadingEmployee } = useFetchUserByIdQuery<
    EmployeeLike & NameFields & { teamManager?: EmployeeLike & NameFields }
  >(id, Boolean(id));
  const { data: teamManagersHistory = [] } = useFetchTeamManagerHistoryQuery<TeamManagerHistoryItem[]>(id, Boolean(id));
  const updateTeamManagerMutation = useUpdateTeamManagerMutation();
  const isSubmitting = updateTeamManagerMutation.isPending;

  const employee = useMemo(
    () =>
      employeeData
        ? {
            ...employeeData,
            name: extractFullName(employeeData),
            currentTeamManager: employeeData.teamManager ? extractFullName(employeeData.teamManager) : 'None',
          }
        : ({} as EmployeeLike & { name?: string; currentTeamManager?: string }),
    [employeeData]
  );

  const employeeDebounceOptions = useEmployeeDebounceOptions(null, {
    roleIds: ROLES_IDS.TEAM_MANAGER,
    fields: USER_FETCH_FIELDS.ROLES,
  });

  const getPageTitle = (employeeData: NameFields | null) => {
    const employeeName = employeeData ? extractFullName(employeeData) : '';
    const pageTitle = employeeName ? `${employeeName}'s team manager` : 'Team Manager';

    return pageTitle;
  };

  useEffect(() => {
    setTitle('Team Manager');
  }, [setTitle]);

  const handleChange = (event: { target: { name: string; value: number | string } }) => {
    const selectedValue = event && event.target && event.target.value;
    const selectedFieldName = event && event.target && event.target.name;

    if (selectedFieldName === 'teamManager') {
      setSelectedTeamManager(selectedValue);
    } else if (selectedFieldName === 'transitionDate') {
      setTransitionDate(String(selectedValue));
    }

    setErrors({
      ...errors,
      [`${selectedFieldName}`]: false,
    });
  };

  useEffect(() => {
    if (employeeData) {
      window.document.title = getPageTitle(employeeData);
    }
  }, [employeeData]);

  const handleSubmit = () => {
    if (!selectedTeamManager || !transitionDate) {
      return setErrors({
        teamManager: !selectedTeamManager,
        transitionDate: !transitionDate,
      });
    }

    const teamManager = {
      id: selectedTeamManager,
      transitionDate,
    };

    updateTeamManagerMutation.mutate(
      { teamManager, id: id! },
      {
        onSuccess: () => {
          toast.success({
            title: 'Success',
            message: 'Team Manager has been updated successfully.',
          });

          redirectToEmployeeEdit();
        },
      }
    );
  };

  const redirectToEmployeeEdit = () => {
    history.goBack();
  };

  const removeOptionsCallback = useCallback(
    (options: TeamManagerOption[]) => {
      return options.filter((option) => +option.value !== +id);
    },
    [id]
  );

  if (loadingEmployee) {
    return (
      <div className="container loading-container">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className="title profile-wrapper">
        <ProfileHeader employee={employee as ProfileHeaderEmployee} user={props.user} />
      </div>
      <div className="container d-flex fd-row flex-fix name-wrap team-manager-page-container">
        <div className="full-scope-card momr-20 team-manager-form-container">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3>Change Team Manager</h3>
            </div>
          </div>
          <div className="team-manager-form">
            <label className="font-label font-14 label-margin">
              <span className="font-weight-bold dark--text"> Name: </span>
              <span className="span-padding-left">{employee.name}</span>
            </label>

            <label className="font-label font-14 label-margin">
              <span className="font-weight-bold dark--text"> Current Team Manager: </span>
              <span className="span-padding-left">{employee.currentTeamManager}</span>
            </label>

            <DebounceFormDropdown
              isMandatory
              name="teamManager"
              label="Team Manager"
              type={LIST_TYPE.EMPLOYEE}
              debounceOptions={employeeDebounceOptions}
              value={selectedTeamManager}
              error={errors.teamManager ? 'Please Select a Team Manager' : null}
              handleChange={handleChange}
              iconComponent={(option) => (
                <AvatarComponent label={String(option.label ?? '')} value={option as { empId?: string | number }} />
              )}
              isClearable={true}
              removeOptionsCallback={removeOptionsCallback}
            />
            <InputWrapper
              isMandatory={true}
              label="Transition Date"
              error={errors.transitionDate ? 'Please Select a transition date' : null}
            >
              <DatePicker
                date={transitionDate ? moment(transitionDate) : null}
                placeholderText="Pick a Transition Date"
                dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                displayFormat="LL"
                hasError={Boolean(errors.transitionDate)}
                openDirection="up"
                onDateChange={(selectedDate) =>
                  handleChange({
                    target: {
                      name: 'transitionDate',
                      value: moment(selectedDate).format('YYYY-MM-DD'),
                    },
                  })
                }
              />
            </InputWrapper>

            <button
              type="button"
              className="btn btn--outlined-grey f-left card-button mr-10 mb-20"
              onClick={redirectToEmployeeEdit}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary f-right card-button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loading /> : 'Update'}
            </button>
          </div>
        </div>
        <TeamManagersHistory teamManagers={teamManagersHistory} />
      </div>
    </>
  );
};

export default ChangeTeamManager;
